import "./CurrencyRatesPage.scss";

import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    asyncGetCurrencyRatesAction,
    asyncGetOnlineBankingPercentAction,
    asyncSaveAllCurrencyRatesAction,
    asyncUpdateOnlineBankingPercentAction
} from "../../redux/slices/currencyRates/currencyRatesAction";
import Button from "../../components/atoms/Button/Button";
import InputGradient from "../../components/molecules/InputGradient/InputGradient";
import { getFileUrl } from "../../helpers/functions";

const normalizePositiveDecimal = (value: string) => {
    const prepared = value.replace(",", ".").replace(/[^\d.]/g, "");
    const parts = prepared.split(".");

    if (parts.length <= 1) {
        return parts[0] || "";
    }

    return `${parts[0]}.${parts.slice(1).join("")}`;
};

const normalizeSignedDecimal = (value: string) => {
    if (value === "-") {
        return value;
    }

    const isNegative = value.trim().startsWith("-");
    const prepared = value.replace(",", ".").replace(/-/g, "").replace(/[^\d.]/g, "");
    const parts = prepared.split(".");

    let normalized = parts[0] || "";

    if (parts.length > 1) {
        normalized = `${normalized}.${parts.slice(1).join("").slice(0, 2)}`;
    }

    if (!normalized) {
        return isNegative ? "-" : "";
    }

    return `${isNegative ? "-" : ""}${normalized}`;
};

const isNameOptionalCode = (code: string) => {
    const normalizedCode = code.trim().toUpperCase();
    return normalizedCode === "USDT" || normalizedCode.startsWith("SWIFT ");
};

interface ITempCurrency {
    tempId: string;
    name: string;
    code: string;
    value: string;
    icon: string;
    iconFile: File | null;
}

interface ICurrencyDraft {
    id?: number;
    name: string;
    code: string;
    value: string;
    icon: string;
    iconFile: File | null;
}

const CurrencyRatesPage: FC = () => {
    const dispatch = useDispatch<any>();
    const { rates, onlineBankingPercent, isLoading, error } = useSelector((state: any) => state.currencyRates);

    const [currencyDrafts, setCurrencyDrafts] = useState<Record<string, ICurrencyDraft>>({});
    const [newCurrencies, setNewCurrencies] = useState<ITempCurrency[]>([]);
    const [deletedCurrencyIds, setDeletedCurrencyIds] = useState<number[]>([]);
    const [onlineBankingValue, setOnlineBankingValue] = useState("");

    useEffect(() => {
        dispatch(asyncGetCurrencyRatesAction());
        dispatch(asyncGetOnlineBankingPercentAction());
    }, [dispatch]);

    useEffect(() => {
        setCurrencyDrafts((prev) => {
            const next = { ...prev };

            rates.forEach((item: any) => {
                next[String(item.id)] = {
                    id: item.id,
                    name: prev[String(item.id)]?.name ?? item.name ?? "",
                    code: prev[String(item.id)]?.code ?? item.code ?? "",
                    value: prev[String(item.id)]?.value ?? String(item.value ?? ""),
                    icon: prev[String(item.id)]?.icon ?? item.icon ?? "",
                    iconFile: prev[String(item.id)]?.iconFile ?? null,
                };
            });

            Object.keys(next).forEach((key) => {
                if (!rates.find((item: any) => String(item.id) === key)) {
                    delete next[key];
                }
            });

            return next;
        });
    }, [rates]);

    useEffect(() => {
        setOnlineBankingValue(normalizeSignedDecimal(String(onlineBankingPercent?.value || "")));
    }, [onlineBankingPercent]);

    const addCurrencyRow = () => {
        const tempId = `new-currency-${Date.now()}-${Math.random()}`;

        setNewCurrencies((prev) => [
            {
                tempId,
                name: "",
                code: "",
                value: "",
                icon: "",
                iconFile: null,
            },
            ...prev,
        ]);
    };

    const updateCurrencyDraft = (id: string, field: "name" | "code" | "value", value: string) => {
        setCurrencyDrafts((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: field === "value" ? normalizePositiveDecimal(value) : value,
            },
        }));
    };

    const updateNewCurrency = (tempId: string, field: "name" | "code" | "value", value: string) => {
        setNewCurrencies((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        [field]: field === "value" ? normalizePositiveDecimal(value) : value,
                    }
                    : item
            )
        );
    };

    const changeCurrencyIcon = (id: string, file: File | null) => {
        if (!file) {
            return;
        }

        const preview = URL.createObjectURL(file);

        setCurrencyDrafts((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                icon: preview,
                iconFile: file,
            },
        }));
    };

    const changeNewCurrencyIcon = (tempId: string, file: File | null) => {
        if (!file) {
            return;
        }

        const preview = URL.createObjectURL(file);

        setNewCurrencies((prev) =>
            prev.map((item) =>
                item.tempId === tempId
                    ? {
                        ...item,
                        icon: preview,
                        iconFile: file,
                    }
                    : item
            )
        );
    };

    const removeNewCurrency = (tempId: string) => {
        setNewCurrencies((prev) => prev.filter((item) => item.tempId !== tempId));
    };

    const removeCurrency = (id: number) => {
        setDeletedCurrencyIds((prev) => {
            if (prev.includes(id)) {
                return prev;
            }

            return [...prev, id];
        });
    };

    const getIconSrc = (icon?: string) => {
        if (!icon) {
            return "";
        }

        if (/^blob:/.test(icon) || /^data:/.test(icon)) {
            return icon;
        }

        return getFileUrl(icon);
    };

    const handleSaveOnlineBankingPercent = async () => {
        if (isLoading) {
            return;
        }

        if (!onlineBankingValue) {
            return;
        }

        await dispatch(asyncUpdateOnlineBankingPercentAction(normalizeSignedDecimal(onlineBankingValue)));
    };

    const handleSaveAllCurrencies = async () => {
        if (isLoading) {
            return;
        }

        const formData = new FormData();
        const items: Array<{
            id?: number;
            name: string;
            code: string;
            value: string;
            iconFileIndex: number | null;
        }> = [];
        let fileIndex = 0;

        const invalidNewCurrencies: ITempCurrency[] = [];

        for (const item of newCurrencies) {
            if (
                !item.code.trim() ||
                !item.value ||
                (!isNameOptionalCode(item.code) && !item.name.trim())
            ) {
                invalidNewCurrencies.push(item);
                continue;
            }

            let iconFileIndex: number | null = null;

            if (item.iconFile) {
                iconFileIndex = fileIndex;
                formData.append("files", item.iconFile);
                fileIndex += 1;
            }

            items.push({
                name: item.name.trim(),
                code: item.code.trim().toUpperCase(),
                value: item.value,
                iconFileIndex,
            });
        }

        for (const rate of rates) {
            if (deletedCurrencyIds.includes(Number(rate.id))) {
                continue;
            }

            const draft = currencyDrafts[String(rate.id)];

            if (
                !draft?.code.trim() ||
                !draft?.value ||
                (!isNameOptionalCode(draft.code) && !draft.name.trim())
            ) {
                continue;
            }

            let iconFileIndex: number | null = null;

            if (draft.iconFile) {
                iconFileIndex = fileIndex;
                formData.append("files", draft.iconFile);
                fileIndex += 1;
            }

            items.push({
                id: Number(rate.id),
                name: draft.name.trim(),
                code: draft.code.trim().toUpperCase(),
                value: draft.value,
                iconFileIndex,
            });
        }

        formData.append("items", JSON.stringify(items));
        formData.append("deletedIds", JSON.stringify(deletedCurrencyIds));

        const result = await dispatch(asyncSaveAllCurrencyRatesAction(formData));

        if (!result?.error) {
            setNewCurrencies(invalidNewCurrencies);
            setDeletedCurrencyIds([]);
            dispatch(asyncGetCurrencyRatesAction());
        }
    };

    const currencyItems = useMemo(() => {
        return [
            ...newCurrencies.map((item) => ({
                ...item,
                isNew: true,
            })),
            ...rates
                .filter((item: any) => !deletedCurrencyIds.includes(Number(item.id)))
                .map((item: any) => ({
                    ...item,
                    isNew: false,
                })),
        ];
    }, [newCurrencies, rates, deletedCurrencyIds]);

    return (
        <div className={"main-admin__content currency-rates-page"}>
            <div className="main-admin__header">
                <h1 style={{ color: "#ffffff", marginBottom: "20px" }}>Настройки валют и процентов</h1>
            </div>

            {error && (
                <p className="currency-rates-page__error">
                    {error}
                </p>
            )}

            <div className="currency-rates-page__grid">
                <div className="currency-rates-page__column">
                    <div className="main-admin__card currency-rates-page__card">
                        <div className="main-admin__card-title">Процент онлайн банкинга</div>

                        <div className="main-admin__card-content">
                            <div className="currency-rates-page__percent-row">
                                <InputGradient
                                    className="currency-rates-page__input"
                                    name="online-banking-type"
                                    placeholder="Тип"
                                    value="ONLINE_BANKING"
                                />

                                <InputGradient
                                    className="currency-rates-page__input"
                                    name="online-banking-value"
                                    placeholder="Значение"
                                    value={onlineBankingValue}
                                    onChange={(e) => setOnlineBankingValue(normalizeSignedDecimal(e.target.value))}
                                />

                                <div className="currency-rates-page__row-actions">
                                    <Button
                                        type="button"
                                        name="Сохранить"
                                        onClick={handleSaveOnlineBankingPercent}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="currency-rates-page__column">
                    <div className="currency-rates-page__top-action">
                        <Button
                            type="button"
                            name="Добавить валюту"
                            onClick={addCurrencyRow}
                        />
                        <Button
                            type="button"
                            name="Сохранить все"
                            onClick={handleSaveAllCurrencies}
                        />
                    </div>

                    <div className="main-admin__card currency-rates-page__card">
                        <div className="main-admin__card-title">Валюты</div>

                        <div className="main-admin__card-content">
                            {!currencyItems.length && (
                                <p className="currency-rates-page__empty">Валюты не найдены</p>
                            )}

                            {currencyItems.map((item: any) => {
                                const current = item.isNew
                                    ? item
                                    : currencyDrafts[String(item.id)] || {
                                    id: item.id,
                                    name: item.name || "",
                                    code: item.code || "",
                                    value: String(item.value || ""),
                                    icon: item.icon || "",
                                    iconFile: null,
                                };

                                return (
                                    <div
                                        key={item.isNew ? item.tempId : item.id}
                                        className="currency-rates-page__currency-row"
                                    >
                                        <label className="currency-rates-page__icon">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) =>
                                                    item.isNew
                                                        ? changeNewCurrencyIcon(item.tempId, e.target.files?.[0] || null)
                                                        : changeCurrencyIcon(String(item.id), e.target.files?.[0] || null)
                                                }
                                            />
                                            {current.icon ? (
                                                <img src={getIconSrc(current.icon)} alt={current.code || current.name || "icon"} />
                                            ) : (
                                                <div className="currency-rates-page__icon-placeholder">Иконка</div>
                                            )}
                                            <div className="currency-rates-page__icon-overlay">
                                                Изменить
                                            </div>
                                        </label>

                                        <InputGradient
                                            className="currency-rates-page__input"
                                            name={`currency-name-${item.isNew ? item.tempId : item.id}`}
                                            placeholder="Название"
                                            value={current.name}
                                            onChange={(e) =>
                                                item.isNew
                                                    ? updateNewCurrency(item.tempId, "name", e.target.value)
                                                    : updateCurrencyDraft(String(item.id), "name", e.target.value)
                                            }
                                        />

                                        <InputGradient
                                            className="currency-rates-page__input"
                                            name={`currency-code-${item.isNew ? item.tempId : item.id}`}
                                            placeholder="Код валюты"
                                            value={current.code}
                                            onChange={(e) =>
                                                item.isNew
                                                    ? updateNewCurrency(item.tempId, "code", e.target.value.toUpperCase())
                                                    : updateCurrencyDraft(String(item.id), "code", e.target.value.toUpperCase())
                                            }
                                        />

                                        <InputGradient
                                            className="currency-rates-page__input"
                                            name={`currency-value-${item.isNew ? item.tempId : item.id}`}
                                            placeholder="Значение"
                                            value={current.value}
                                            onChange={(e) =>
                                                item.isNew
                                                    ? updateNewCurrency(item.tempId, "value", e.target.value)
                                                    : updateCurrencyDraft(String(item.id), "value", e.target.value)
                                            }
                                        />

                                        <div className="currency-rates-page__row-actions">
                                            <Button
                                                color={'red'}
                                                type="button"
                                                name="Удалить"
                                                onClick={() =>
                                                    item.isNew
                                                        ? removeNewCurrency(item.tempId)
                                                        : removeCurrency(Number(item.id))
                                                }
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CurrencyRatesPage;